import React, { useState, useEffect } from "react";
import { postAPI } from "../../utils/common";
import { REPORTING } from "../../constants/api";
import "./ReportsPage.css";
import LayoutHeader from "../inc/LayoutHeader";
import LayoutFooter from "../inc/LayoutFooter";
import { Card, Layout } from "antd";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import { PieChart, Pie, Cell } from "recharts";

const ReportsPageV2 = () => {
  const { Content } = Layout;
  const [reports, setReports] = useState(null);
  const [startDate, setStartDate] = useState(getDefaultStartDate());
  const [endDate, setEndDate] = useState(getDefaultEndDate());

  // Default start and end dates
  function getDefaultStartDate() {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), 1)
      .toISOString()
      .slice(0, 10);
  }

  function getDefaultEndDate() {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth() + 1, 0)
      .toISOString()
      .slice(0, 10);
  }

  // Fetch the reports based on date filters
  const fetchReports = () => {
    postAPI(
      REPORTING,
      { start_date: startDate, end_date: endDate },
      (data) => {
        if (data.status) {
          setReports(data.data);
        }
      },
      (err) => console.log(err)
    );
  };

  // Fetch reports whenever the component is mounted or the date filters change
  useEffect(() => {
    fetchReports();
  }, [startDate, endDate]);

  // Render the chart for occupancy rate and revenue trends
  const processBookingTrends = (trends, revenue, totalRooms) => {
    return trends.map((trend) => ({
      date: trend.date,
      occupancy_rate: (trend.bookings_count / totalRooms) * 100,
      revenue: revenue, // Use actual revenue for each date if applicable
      bookings_count: trend.bookings_count,
    }));
  };

  const renderChart = (trends) => {
    // Assuming you fetch `revenue` and `totalRooms` from the response or elsewhere
    const processedTrends = processBookingTrends(
      reports.booking_trends,
      reports.revenue,
      100
    ); // Example: 100 rooms in total

    return (
      <ResponsiveContainer width="100%" height={400}>
        <LineChart data={processedTrends}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="date" />
          <YAxis />
          <Tooltip />
          <Legend />
          <Line type="monotone" dataKey="occupancy_rate" stroke="#8884d8" />
          <Line type="monotone" dataKey="revenue" stroke="#82ca9d" />
        </LineChart>
      </ResponsiveContainer>
    );
  };

  // Render the pie chart for booking sources
  const renderPieChart = (data) => {
    // Transform the data into the required format for Pie chart
    const transformedData = data.map((entry) => ({
      name: entry.booking_source, // Booking source name
      value: entry.count, // Booking count
    }));

    return (
      <PieChart width={600} height={600}>
        <Pie
          dataKey="value"
          isAnimationActive={false}
          data={transformedData}
          outerRadius={250}
          fill="#8884d8"
          label
        >
          {transformedData.map((entry, index) => (
            <Cell key={`cell-${index}`} fill={getRandomColor()} />
          ))}
        </Pie>
        <Tooltip />
      </PieChart>
    );
  };

  // Helper function to generate random colors for pie chart segments
  const getRandomColor = () => {
    const letters = "0123456789ABCDEF";
    let color = "#";
    for (let i = 0; i < 6; i++) {
      color += letters[Math.floor(Math.random() * 16)];
    }
    return color;
  };

  return (
    <Layout>
      <LayoutHeader />
      <Content>
        <Card
          title="Booking Statistics"
          extra={
            <>
              <div className="filters">
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                />
                <input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                />
                <button onClick={fetchReports}>Filter</button>
              </div>
            </>
          }
        >
          <div className="reports-container">
            {reports ? (
              <>
                <div className="section">
                  <h2>Occupancy & Revenue Overview</h2>
                  {renderChart(reports.trends)}
                </div>

                <div
                  className="card-group"
                  style={{ justifyContent: "center", alignItems: "center" }}
                >
                  <div className="section">
                    <h2>Booking Sources</h2>
                    {renderPieChart(reports.booking_sources)}
                  </div>

                  <div>
                    <div className="section">
                      <h2>Top Room Types</h2>
                      <div className="card-group">
                        {reports.top_room_types &&
                        reports.top_room_types.length > 0 ? (
                          reports.top_room_types.map((room, i) => (
                            <div className="card" key={i}>
                              <h3>{room.room_type}</h3>
                              <p>{room.count} bookings</p>
                            </div>
                          ))
                        ) : (
                          <div className="card">
                            <p>No data</p>
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="section">
                      <h2>Cancelled Bookings</h2>
                      <div className="card">
                        <h3>{reports.cancelled_bookings}</h3>
                        <p>Cancelled</p>
                      </div>
                    </div>
                    <div className="section">
                      <h2>Average Stay Duration</h2>
                      <div className="card">
                        <h3>{reports.average_stay_days} days</h3>
                        <p>Avg Stay Duration</p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="section">
                  <h2>Booking Status</h2>
                  <div className="card-group">
                    {Object.entries(reports.booking_status_count).map(
                      ([key, val]) => (
                        <div className="card" key={key}>
                          <h3>{val}</h3>
                          <p>{key}</p>
                        </div>
                      )
                    )}
                  </div>
                </div>
                <div className="section">
                  <h2>Payment Methods</h2>
                  <div className="card-group">
                    <div className="card">
                      <h3>{reports.payment_methods.cash} ₹</h3>
                      <p>Cash Payments</p>
                    </div>
                    <div className="card">
                      <h3>{reports.payment_methods.online} ₹</h3>
                      <p>Online Payments</p>
                    </div>
                    <div className="card">
                      <h3 style={{ color: "red" }}>₹ {reports.total_discount_given ?? 0}</h3>
                      <p>Total Discount Given</p>
                    </div>
                  </div>
                </div>
              </>
            ) : (
              <p>Loading...</p>
            )}
          </div>
        </Card>
      </Content>
      <LayoutFooter />
    </Layout>
  );
};

export default ReportsPageV2;
